import React, { useRef, useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Menu } from "primereact/menu";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteCompany,
  deleteMultipleCompanies,
  getCompanies,
} from "../../store/AsyncMethods/CompanyMethod";

export default function CompanyTable() {
  const { allCompanies } = useSelector((state) => state.CompanyReducer); // Assuming CompanyReducer and companies state
  const { user } = useSelector((state) => state.AuthReducer);
  const { t } = useTranslation();

  const menu = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDelDialog, setShowDelDialog] = useState(false);
  const [showMultipleDelDialog, setShowMultipleDelDialog] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState(null); // For multiple selection

  useEffect(() => {
    if (user && user.role_name === 'Admin') { 
      dispatch(getCompanies());
    }
  }, [user, dispatch]);


  const acceptSingleDelete = () => {
    if (selectedItem && selectedItem.id) {
      dispatch(deleteCompany(selectedItem.id)).then((success) => {
        if (success) {
          setSelectedItem(null);
          dispatch(getCompanies());
        }
      });
    }
    setShowDelDialog(false);
  };

  const rejectSingleDelete = () => {
    setShowDelDialog(false);
  };

  const acceptMultipleDelete = () => {
    if (selectedCompanies && selectedCompanies.length > 0) {
      const idsArray = selectedCompanies.map((company) => company.id);
      dispatch(deleteMultipleCompanies(idsArray)).then((success) => {
        if (success) {
          setSelectedCompanies(null);
          dispatch(getCompanies());
        }
      });
    }
    setShowMultipleDelDialog(false);
  };

  const rejectMultipleDelete = () => {
    setShowMultipleDelDialog(false);
  };

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
  };

  const paginatorLeft = (
    <Button
      label={t("delete")}
      icon="pi pi-trash"
      className="p-red-btn !mr-auto"
      onClick={() => setShowMultipleDelDialog(true)}
      disabled={!selectedCompanies || selectedCompanies.length === 0}
    />
  );

  const renderHeader = () => {
    const value = filters["global"] ? filters["global"].value : "";
    return (
      <div className="flex justify-between items-center">
        <span className="p-input-icon-left flex w-10/12 sm:w-8/12 md:w-6/12 lg:w-4/12 xl:w-3/12 ">
          <i className="pi pi-search" />
          <InputText
            className="w-full p-primary-input pl-10"
            type="search"
            value={value || ""}
            onChange={onGlobalFilterChange}
            placeholder={t("search_companies")}
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  const actionMenuItems = [
    {
      label: t("edit"),
      icon: "pi pi-pencil",
      command: () => {
        if (selectedItem && selectedItem.id) {
          navigate(`/edit-company/${selectedItem.id}`);
        }
      },
    },
    {
      label: t("delete"),
      icon: "pi pi-trash",
      command: () => {
        if (selectedItem) {
          setShowDelDialog(true);
        }
      },
    },
  ];

  const actionBodyTemplate = (rowData) => (
    <>
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-rounded p-button-text"
        onClick={(event) => {
          menu.current.toggle(event);
          setSelectedItem(rowData); 
        }}
        aria-controls="popup_menu"
        aria-haspopup
      />
    </>
  );

  const dateBodyTemplate = (rowData, field) => {
    const date = new Date(rowData[field]);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };
  
  const tenantIdBodyTemplate = (rowData) => {
    return rowData.tenant_first_name + " " + rowData.tenant_last_name; 
  };

  return (
    <div>
      <Menu model={actionMenuItems} popup ref={menu} id="popup_menu" />
      <ConfirmDialog
        visible={showDelDialog}
        onHide={rejectSingleDelete}
        message={t("confirm_delete_message_company")}
        header={t("delete_confirmation")}
        icon="pi pi-info-circle"
        accept={acceptSingleDelete}
        reject={rejectSingleDelete}
        acceptClassName="p-red-btn"
        rejectClassName="p-button-text"
        pt={{
            root: { className: "w-9/12 sm:w-7/12 md:w-5/12 lg:w-4/12 xl:w-3/12" },
            icon: { className: "mx-2" },
          }}
      />
      <ConfirmDialog
        visible={showMultipleDelDialog}
        onHide={rejectMultipleDelete}
        message={t("confirm_delete_multiple_companies")}
        header={t("delete_confirmation")}
        icon="pi pi-info-circle"
        accept={acceptMultipleDelete}
        reject={rejectMultipleDelete}
        acceptClassName="p-red-btn"
        rejectClassName="p-button-text"
         pt={{
            root: { className: "w-9/12 sm:w-7/12 md:w-5/12 lg:w-4/12 xl:w-3/12" },
            icon: { className: "mx-2" },
          }}
      />

      <div className="card shadow-sm">
        <DataTable
          value={allCompanies} 
          tableStyle={{ minWidth: "50rem" }}
          stripedRows
          paginator
          rows={5} 
          rowsPerPageOptions={[5, 10, 25, 50]}
          header={header}
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          selection={selectedCompanies}
          onSelectionChange={(e) => setSelectedCompanies(e.value)}
          dataKey="id" 
          onRowClick={(e) => navigate(`/edit-company/${e.data.id}`)} 
          paginatorLeft={paginatorLeft}
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
          <Column
            field="company_name"
            header={t("company_name")}
            sortable
            style={{ minWidth: "200px" }}
          />
          <Column
            field="tenant_name"
            header={t("tenant_name")}
            body={tenantIdBodyTemplate}
            sortable
            style={{ minWidth: "100px" }}
          />
          <Column
            field="created_at"
            header={t("created_at")}
            body={(rowData) => dateBodyTemplate(rowData, "created_at")}
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="updated_at"
            header={t("updated_at")}
            body={(rowData) => dateBodyTemplate(rowData, "updated_at")}
            sortable
            style={{ minWidth: "150px" }}
          />
          {/* <Column
            header={t("actions")}
            body={actionBodyTemplate}
            exportable={false}
            style={{ minWidth: "8rem" }}
            align="center"
          /> */}
        </DataTable>
      </div>
    </div>
  );
} 